import { PdfPage } from 'components/PdfExportConfigDialog/hooks/types';
import { Logger } from 'utilities';
import { capturePage } from './captureIframe';
import { PageResult } from './types';

// Fallback used when the browser exposes no usable capability signals, or
// when an explicit concurrency arg is invalid. Matches the previous static
// default so older/unknown runtimes do not regress.
export const DEFAULT_CONCURRENCY = 2;
export const MIN_CONCURRENCY = 1;
export const MAX_CONCURRENCY = 8;

// Optional capability signals we probe on `navigator`. `hardwareConcurrency`
// is widely supported; `deviceMemory` is Chromium-only; `cpuPerformance` is
// non-standard and treated as a best-effort performance hint when present.
interface NavigatorCapabilities {
  hardwareConcurrency?: number;
  deviceMemory?: number;
  cpuPerformance?: number;
}

// Each PDF page capture spawns a hidden iframe + an html2canvas pass at 2x
// scale, so the concurrency picked per CPU signal stays conservative even on
// strong machines.
const concurrencyFromCpu = (cores: number): number => {
  if (cores <= 2) {
    return 1;
  }
  if (cores <= 4) {
    return 2;
  }
  if (cores <= 8) {
    return 3;
  }
  if (cores <= 12) {
    return 4;
  }
  return 6;
};

// Memory is the more common bottleneck at scale: parallel captures at 2x
// scale quickly cross hundreds of MB of transient canvases.
const concurrencyFromMemory = (memoryGb: number): number => {
  if (memoryGb <= 2) {
    return 1;
  }
  if (memoryGb <= 4) {
    return 2;
  }
  if (memoryGb <= 8) {
    return 3;
  }
  if (memoryGb <= 16) {
    return 4;
  }
  return 6;
};

const performanceModifier = (value: number | undefined): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  if (value < 1) {
    return -1;
  }
  if (value > 2) {
    return 1;
  }
  return 0;
};

/**
 * Picks a default parallel-capture concurrency from browser-reported machine
 * resources (`navigator.hardwareConcurrency`, `navigator.deviceMemory`, and
 * the non-standard `navigator.cpuPerformance` hint when present). The result
 * is clamped to `[MIN_CONCURRENCY, MAX_CONCURRENCY]`. When no signals are
 * usable (e.g. SSR or legacy browsers) returns `DEFAULT_CONCURRENCY`.
 */
export const getDefaultConcurrency = (): number => {
  if (typeof navigator === 'undefined') {
    return DEFAULT_CONCURRENCY;
  }

  const nav = navigator as Navigator & NavigatorCapabilities;
  const cores = nav.hardwareConcurrency;
  const memoryGb = nav.deviceMemory;
  const perf = nav.cpuPerformance;

  const tiers: number[] = [];
  if (typeof cores === 'number' && cores > 0) {
    tiers.push(concurrencyFromCpu(cores));
  }
  if (typeof memoryGb === 'number' && memoryGb > 0) {
    tiers.push(concurrencyFromMemory(memoryGb));
  }

  if (tiers.length === 0) {
    return DEFAULT_CONCURRENCY;
  }

  const base = Math.min(...tiers);
  const suggested = base + performanceModifier(perf);

  return Math.max(MIN_CONCURRENCY, Math.min(MAX_CONCURRENCY, suggested));
};

// Number of times the orchestrator will retry a single page if its iframe
// stalls (waitForReadiness exits with reason='stalled' because the React app
// inside the iframe is hung). Most stalls recover on a fresh iframe, where
// the second attempt gets new connections, a fresh React Query cache, etc.
const MAX_STALL_RETRIES = 1;

interface RunOrchestratorArgs {
  pages: PdfPage[];
  signal: AbortSignal;
  onPageUpdate: (result: PageResult) => void;
  concurrency?: number;
}

/**
 * Drives the capture pipeline with a configurable concurrency.
 * Order of returned results matches input order, regardless of completion order.
 */
export const runCapturePipeline = async ({
  pages,
  signal,
  onPageUpdate,
  concurrency,
}: RunOrchestratorArgs): Promise<PageResult[]> => {
  const results: PageResult[] = pages.map((p, index) => ({
    index,
    title: p.title,
    exportTarget: p.exportTarget,
    url: p.url,
    status: 'pending',
  }));

  let nextIndex = 0;

  const takeNext = (): number | null => {
    if (signal.aborted) {
      return null;
    }
    if (nextIndex >= pages.length) {
      return null;
    }
    const idx = nextIndex;
    nextIndex += 1;
    return idx;
  };

  const emit = (idx: number) => {
    onPageUpdate({ ...results[idx] });
  };

  const worker = async () => {
    while (!signal.aborted) {
      const idx = takeNext();
      if (idx === null) {
        return;
      }
      const page = pages[idx];

      results[idx] = { ...results[idx], status: 'loading', startedAt: performance.now() };
      emit(idx);

      // eslint-disable-next-line no-await-in-loop
      let verdict = await capturePage({ url: page.url, exportTarget: page.exportTarget, signal });
      // Retry once on a fresh iframe if the React app inside hung (stall
      // recovery). The teardown of the previous iframe in capturePage frees
      // the connections/cache that may have been the cause.
      let retries = 0;
      while (
        retries < MAX_STALL_RETRIES &&
        verdict.kind === 'failed' &&
        verdict.reason === 'stalled' &&
        !signal.aborted
      ) {
        retries += 1;
        Logger.warn(`Retrying stalled capture for ${page.exportTarget} (attempt ${retries + 1})`);
        // eslint-disable-next-line no-await-in-loop
        verdict = await capturePage({ url: page.url, exportTarget: page.exportTarget, signal });
      }

      if (signal.aborted || verdict.kind === 'canceled') {
        results[idx] = { ...results[idx], status: 'canceled', finishedAt: performance.now() };
        emit(idx);
        return;
      }

      if (verdict.kind === 'ready') {
        results[idx] = {
          ...results[idx],
          status: 'done',
          finishedAt: performance.now(),
          image: verdict.image,
        };
        emit(idx);
        // Release this worker's reference to the captured Blob now that the
        // consumer has its own copy via emit's spread. Holding it here would
        // keep every captured Blob alive in `results` until the entire
        // pipeline resolves, defeating the streaming PDF assembly downstream.
        results[idx] = { ...results[idx], image: null };
      } else if (verdict.kind === 'missing') {
        results[idx] = {
          ...results[idx],
          status: 'skipped',
          finishedAt: performance.now(),
          reason: 'target not present',
          image: null,
        };
        emit(idx);
      } else {
        results[idx] = {
          ...results[idx],
          status: 'failed',
          finishedAt: performance.now(),
          reason: verdict.reason,
          image: null,
        };
        emit(idx);
      }
    }
  };

  const resolvedConcurrency = Number.isFinite(concurrency) ? Number(concurrency) : getDefaultConcurrency();
  const clampedConcurrency = Math.max(
    MIN_CONCURRENCY,
    Math.min(MAX_CONCURRENCY, Math.floor(resolvedConcurrency) || DEFAULT_CONCURRENCY)
  );

  const workers: Promise<void>[] = [];
  const workerCount = Math.min(clampedConcurrency, pages.length);
  for (let i = 0; i < workerCount; i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  return results;
};
